import { useEffect } from 'react';

// Comprehensive SEO configuration for each page
// Optimized for Google, Bing, AI engines (ChatGPT, Claude, Perplexity), and voice search
const seoConfig = {
  home: {
    title: 'BevRelay | #1 Beverage Supplier Network - Connect Brands, Manufacturers & Buyers',
    description: 'BevRelay connects beverage suppliers, manufacturers, and distributor reps with qualified buyers. Join 500+ beverage professionals. Supplier memberships from $499/year. Distributor reps earn $200+ per placement. Free buyer access.',
    keywords: 'beverage suppliers, beverage manufacturers, beverage distribution network, distributor rep jobs, B2B beverage marketplace, craft spirits suppliers, wine distribution, beverage buyer network, beverage coalition, supplier introductions, beverage brand expansion, alcohol distribution network, beverage sales rep opportunities, beverage co-packers, beverage packaging suppliers',
    canonical: '/',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'BevRelay - #1 Beverage Supplier Network',
      description: 'Connect beverage brands, manufacturers, and distributor reps with qualified buyers through trusted introductions and coalition presentations',
      url: 'https://bevrelay.com/',
      mainEntity: {
        '@type': 'Organization',
        name: 'BevRelay',
        description: 'Supplier-owned beverage relationship network'
      }
    }
  },
  suppliers: {
    title: 'For Beverage Suppliers | BevRelay - Expand Distribution Through Coalition',
    description: 'Join BevRelay supplier coalition to expand your beverage brand nationwide. Protected relationship sharing, permissioned introductions, and outcome tracking. Starting at $499/year. Craft spirits, wine, RTD brands welcome.',
    keywords: 'beverage supplier network, supplier coalition membership, beverage distribution expansion, craft spirits supplier, wine supplier network, RTD beverage suppliers, beverage brand growth, wholesale beverage suppliers, alcohol supplier marketplace, supplier relationship network',
    canonical: '/suppliers',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'BevRelay Supplier Coalition Membership',
      description: 'Expand your beverage brand reach through coalition portfolio presentations and shared buyer relationships',
      url: 'https://bevrelay.com/suppliers',
      provider: {
        '@type': 'Organization',
        name: 'BevRelay'
      },
      serviceType: 'B2B Beverage Supplier Network',
      areaServed: 'United States',
      offers: {
        '@type': 'Offer',
        priceRange: '$499 - $1,500/year',
        priceCurrency: 'USD'
      }
    }
  },
  buyers: {
    title: 'For Beverage Buyers | BevRelay - Free Access to Curated Suppliers',
    description: 'Free buyer access to discover premium beverage suppliers. Browse curated craft spirits, wine, and beverage brands. Request meetings and samples through trusted introductions. No cost, no commitment.',
    keywords: 'beverage buyers network, find beverage suppliers, curated beverage brands, wholesale beverage sourcing, craft spirits buyers, wine buyers marketplace, beverage procurement platform, bar beverage suppliers, restaurant beverage sourcing, hotel beverage purchasing',
    canonical: '/buyers',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'BevRelay Buyer Access',
      description: 'Free access for beverage buyers to discover curated suppliers and request meetings or samples',
      url: 'https://bevrelay.com/buyers',
      provider: {
        '@type': 'Organization',
        name: 'BevRelay'
      },
      serviceType: 'B2B Beverage Buyer Platform',
      areaServed: 'United States',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free buyer access'
      }
    }
  },
  distributorReps: {
    title: 'Distributor Rep Jobs & Incentives | BevRelay - Earn $200+ Per Placement',
    description: 'Distributor sales reps: earn direct incentives for beverage placements. $200 for new BTG programs, $100 for features. Get paid via Venmo/PayPal. Join free. Work with your existing accounts and relationships.',
    keywords: 'distributor rep jobs, beverage sales rep incentives, alcohol distributor rep, liquor rep jobs, wine sales rep opportunities, beverage placement incentives, distributor sales jobs, rep incentive program, beverage industry jobs, sales rep commission',
    canonical: '/distributor-reps',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: 'Beverage Distributor Sales Rep - Incentive Program',
      description: 'Earn direct incentives ($200 for new placements, $100 for features) for beverage placements at your accounts. Work with your existing relationships. Get paid via Venmo/PayPal. Free to join.',
      datePosted: '2026-01-01',
      validThrough: '2027-12-31',
      employmentType: 'CONTRACTOR',
      hiringOrganization: {
        '@type': 'Organization',
        name: 'BevRelay',
        sameAs: 'https://bevrelay.com'
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'US'
        }
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: {
          '@type': 'QuantitativeValue',
          minValue: 100,
          maxValue: 200,
          unitText: 'per placement'
        }
      },
      jobBenefits: 'Direct payment via Venmo/PayPal, flexible schedule, work your existing accounts',
      skills: 'Beverage industry experience, existing account relationships, sales skills'
    }
  },
  buildBrand: {
    title: 'Build a Beverage Brand | BevRelay - Source-to-Shelf Brand Builder Tool',
    description: 'Build any beverage brand from source to shelf. Find manufacturers, packaging suppliers, and label designers. Spirits, wine, RTDs, functional beverages. One-time $199 access. Get personalized supplier recommendations.',
    keywords: 'build beverage brand, beverage manufacturing, start spirits brand, create wine brand, RTD beverage development, beverage packaging suppliers, label design beverage, beverage co-packer, beverage startup, beverage brand builder',
    canonical: '/build-brand',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'BevRelay Brand Builder',
      description: 'Tool to help entrepreneurs build beverage brands with manufacturer and packaging supplier recommendations',
      url: 'https://bevrelay.com/build-brand',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '199',
        priceCurrency: 'USD',
        description: 'One-time Brand Builder Access'
      }
    }
  },
  aiWorkforce: {
    title: 'AI Workforce for Beverage Brands | BevRelay - Managed AI Services',
    description: 'AI-powered services for beverage brands. Social media management, sales outreach, operations support, compliance prep. Monthly AI support starting at $99/month. Scale your beverage brand with AI.',
    keywords: 'AI beverage marketing, beverage brand AI services, AI social media beverage, beverage compliance AI, AI sales outreach, beverage operations AI, managed AI services, AI marketing beverages, beverage brand automation',
    canonical: '/ai-workforce',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'BevRelay AI Workforce',
      description: 'Managed AI services for beverage brands including marketing, sales, operations, and compliance support',
      url: 'https://bevrelay.com/ai-workforce',
      provider: {
        '@type': 'Organization',
        name: 'BevRelay'
      },
      serviceType: 'AI Marketing and Operations Services',
      offers: {
        '@type': 'Offer',
        priceRange: 'Starting at $99/month',
        priceCurrency: 'USD'
      }
    }
  },
  targetList: {
    title: 'Target List - Top Beverage Buyers in 50 US Cities | BevRelay',
    description: 'Beverage startup target list: access top on-premise and off-premise buyers in 50 US cities. Launch your spirit, wine, or RTD brand with direct buyer contacts. Coalition members get Top 100 + emails.',
    keywords: 'beverage startup, launch spirit brand, beverage distribution, craft spirits distribution, beverage buyers list, RTD beverage launch, wine distribution contacts, beverage buyer database, on-premise buyers, off-premise buyers, liquor store contacts, bar beverage buyers, restaurant beverage purchasing, beverage startup target list, spirit brand launch, craft beverage distribution',
    canonical: '/target-list',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: 'BevRelay Target List - Beverage Buyer Database',
      description: 'Comprehensive database of top beverage buyers across 50 US cities. Includes on-premise (restaurants, bars, hotels) and off-premise (retail, liquor stores) accounts for beverage startup distribution.',
      url: 'https://bevrelay.com/target-list',
      creator: {
        '@type': 'Organization',
        name: 'BevRelay'
      },
      spatialCoverage: 'United States',
      keywords: ['beverage startup', 'launch spirit', 'craft spirits', 'beverage distribution', 'RTD launch', 'wine distribution']
    }
  },
  about: {
    title: 'About BevRelay | Supplier-Owned Beverage Relationship Network',
    description: 'BevRelay is a supplier-owned coalition network founded by beverage industry professionals. We connect suppliers and buyers through trusted introductions. Not a distributor or marketplace.',
    keywords: 'about bevrelay, beverage network founders, supplier coalition history, B2B beverage platform, beverage industry network, beverage relationship platform',
    canonical: '/about',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'About BevRelay',
      description: 'Learn about the BevRelay supplier-owned coalition network and our mission',
      url: 'https://bevrelay.com/about',
      mainEntity: {
        '@type': 'Organization',
        name: 'BevRelay',
        foundingDate: '2024',
        description: 'Supplier-owned beverage relationship network'
      }
    }
  },
  requestAccess: {
    title: 'Join BevRelay | Request Access - Suppliers & Buyers Welcome',
    description: 'Request access to BevRelay beverage network. Free buyer access. Supplier memberships from $499/year. Join the national coalition and start growing your beverage business today.',
    keywords: 'join bevrelay, request access bevrelay, beverage network signup, supplier application, buyer registration, beverage coalition membership, beverage network access',
    canonical: '/request-access',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Request Access to BevRelay',
      description: 'Apply for buyer or supplier access to the BevRelay beverage relationship network',
      url: 'https://bevrelay.com/request-access'
    }
  },
  login: {
    title: 'Sign In | BevRelay Dashboard',
    description: 'Sign in to your BevRelay account. Access your supplier dashboard, buyer portal, or rep incentive board.',
    keywords: 'bevrelay login, bevrelay signin, bevrelay dashboard, supplier login, buyer login',
    canonical: '/login',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'BevRelay Sign In',
      url: 'https://bevrelay.com/login'
    }
  },
  register: {
    title: 'Create Account | BevRelay - Join the Beverage Network',
    description: 'Create your BevRelay account to access the beverage supplier and buyer network. Choose your role: Supplier, Buyer, or Distributor Rep.',
    keywords: 'bevrelay register, create bevrelay account, bevrelay signup, join bevrelay',
    canonical: '/register',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Create BevRelay Account',
      url: 'https://bevrelay.com/register'
    }
  },
  aiBlueprint: {
    title: 'AI Beverage Blueprint Builder | BevRelay - Generate Your Launch Plan',
    description: 'AI-powered beverage company blueprint generator. Get formula direction, packaging strategy, co-packer targets, distributor approach, marketing plan, and 90-day launch action steps. Part of Brand Builder Access.',
    keywords: 'beverage blueprint, AI beverage plan, launch spirit brand, beverage startup plan, beverage business plan generator, craft spirits launch, RTD beverage launch plan, beverage go-to-market strategy',
    canonical: '/ai-blueprint',
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'BevRelay AI Blueprint Builder',
      description: 'AI-powered tool to generate comprehensive beverage company launch blueprints',
      url: 'https://bevrelay.com/ai-blueprint',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: {
        '@type': 'Offer',
        price: '199',
        priceCurrency: 'USD',
        description: 'Included with Brand Builder Access'
      }
    }
  }
};

// Hook to update document head for SEO
export function useSEO(page) {
  useEffect(() => {
    const config = seoConfig[page];
    if (!config) return;

    // Update title
    document.title = config.title;

    // Update or create meta tags
    const updateMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard meta tags
    updateMeta('description', config.description);
    updateMeta('keywords', config.keywords);
    
    // Open Graph
    updateMeta('og:title', config.title, true);
    updateMeta('og:description', config.description, true);
    updateMeta('og:type', config.ogType, true);
    updateMeta('og:url', `https://bevrelay.com${config.canonical}`, true);
    updateMeta('og:site_name', 'BevRelay', true);
    
    // Twitter
    updateMeta('twitter:title', config.title);
    updateMeta('twitter:description', config.description);
    updateMeta('twitter:card', 'summary_large_image');

    // Update canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `https://bevrelay.com${config.canonical}`);

    // Update JSON-LD schema
    let schemaScript = document.querySelector('script[data-page-schema="true"]');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('data-page-schema', 'true');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(config.schema);

  }, [page]);
}

// Export config for direct access
export { seoConfig };
